<script setup lang="ts">
/**
 * Reusable 5-step application form, used in §3 (FormPreview) and §10 (FinalCTA).
 * Two independent instances — each keeps its own step state, form data and
 * submission lifecycle (no shared state between them).
 *
 * Steps: 1 Vorname+Nachname · 2 Skills (multi) · 3 Experience (single) ·
 * 4 E-Mail · 5 Telefon + Einwilligung + submit. Optional CV upload (PDF,
 * max 10MB) lives on step 5 — added per Tom's brief, not present in the mockup
 * (see report).
 *
 * Submission is the same multipart contract as the original application
 * form: fields + optional CV → /api/apply → Join API. `capi_allowed` comes
 * from useCookiebot() marketing consent, unchanged.
 *
 * The 4-line card header (kicker · H2 · two check-items) is centered and uses
 * the same type scale as the page's own section headings (text-lg/xl kicker,
 * 28/40px H2) — matched to the original mockup during the client review.
 */
import { PhCheck } from '@phosphor-icons/vue'
import FormProgress from '~/components/MultiStepForm/FormProgress.vue'
import CheckboxGroup from '~/components/MultiStepForm/CheckboxGroup.vue'
import FormField from '~/components/MultiStepForm/FormField.vue'
import FormTextarea from '~/components/MultiStepForm/FormTextarea.vue'
import FileUpload from '~/components/MultiStepForm/FileUpload.vue'
import { newEventId, getCookie, getFbc } from '~/utils/metaPixel'

const props = withDefaults(
  defineProps<{
    /**
     * §10 overlay variant. The card is solid white in both variants; `overlay`
     * only swaps in a wider, deeper drop shadow. §10 sits on a white overlay
     * (client request), so the shadow — not a background contrast — is what
     * keeps the card's edge readable there.
     */
    overlay?: boolean
  }>(),
  { overlay: false },
)

const { marketingConsent } = useCookiebot()

// Persist the ad-click fbclid (sessionStorage) so it survives page changes
// before the applicant submits.
onMounted(() => {
  getFbc()
})

const currentStep = ref(1)
const submitting = ref(false)
const success = ref(false)
const errorMessage = ref('')

const fields = reactive({
  firstname: '',
  lastname: '',
  email: '',
  telefon: '',
  einwilligung: false,
  skills: [] as string[],
  experience: '',
  note: '',
})
const cv = ref<File | null>(null)

const errors = reactive<Record<string, string>>({})

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const phoneRegex = /^(?:\+[1-9]\d{6,14}|0\d{6,14})$/

const skillOptions = [
  { label: 'Scrum', value: 'Scrum' },
  { label: 'Kanban', value: 'Kanban' },
  { label: 'Multiprojektmanagement', value: 'Multiprojektmanagement' },
  { label: 'Ressourcen- und Kapazitätsplanung', value: 'Ressourcen- und Kapazitätsplanung' },
  { label: 'Ticketsystem', value: 'Ticketsystem' },
  { label: 'Prozessautomatisierung', value: 'Prozessautomatisierung' },
  { label: ' KI im Arbeitskontext', value: ' KI im Arbeitskontext' },
]

const experienceOptions = [
  { label: '1-3 Jahre', value: '1-3 Jahre' },
  { label: '3-5 Jahre', value: '3-5 Jahre' },
  { label: '5+ Jahre', value: '5+ Jahre' },
]

const buttonLabel = computed(() => {
  if (currentStep.value === 5) return 'Jetzt bewerben!'
  if (currentStep.value === 4) return 'zum letzten Schritt'
  return 'Nächster Schritt'
})

const validateStep = (): boolean => {
  Object.keys(errors).forEach((k) => delete errors[k])
  switch (currentStep.value) {
    case 1:
      if (!fields.firstname.trim()) errors.firstname = 'Vorname ist erforderlich'
      if (!fields.lastname.trim()) errors.lastname = 'Nachname ist erforderlich'
      break
    case 4:
      if (!fields.email.trim()) errors.email = 'E-Mail ist erforderlich'
      else if (!emailRegex.test(fields.email)) errors.email = 'Ungültige E-Mail-Adresse'
      break
    case 5:
      if (fields.telefon.trim() && !phoneRegex.test(fields.telefon.trim())) {
        errors.telefon = 'Gültiges Format: +49123456789'
      }
      if (!fields.einwilligung) errors.einwilligung = 'Zustimmung erforderlich'
      break
  }
  return Object.keys(errors).length === 0
}

const nextStep = () => {
  if (!validateStep()) return
  currentStep.value += 1
}

const prevStep = () => {
  if (currentStep.value > 1) currentStep.value -= 1
}

const submit = async () => {
  if (!validateStep()) return

  // Meta Lead: browser Pixel + server CAPI share this event_id for dedup.
  // Fires on every valid step-5 submit, before the server answers.
  const eventId = newEventId()
  try {
    const w = window as any
    if (typeof w.fbq === 'function') {
      w.fbq('track', 'Lead',
        { content_name: 'Lead Projektmanagement', content_category: 'Bewerbung' },
        { eventID: eventId })
    }
  } catch (e) { /* Tracking darf das Formular nie stören */ }

  submitting.value = true
  success.value = false
  errorMessage.value = ''

  const form = new FormData()
  form.append('first_name', fields.firstname)
  form.append('last_name', fields.lastname)
  form.append('email', fields.email)
  form.append('telefon', fields.telefon)
  form.append('einwilligung', String(fields.einwilligung))
  if (fields.skills.length) form.append('skills', fields.skills.join(', '))
  if (fields.experience) form.append('experience', fields.experience)
  if (cv.value) form.append('cv', cv.value)
  form.append('capi_allowed', String(!!marketingConsent.value))
  form.append('event_source_url', window.location.href)
  form.append('event_id', eventId)
  form.append('fbp', getCookie('_fbp'))
  form.append('fbc', getFbc())
  if (fields.note.trim()) form.append('note', fields.note)

  try {
    await $fetch('/api/apply', {
      method: 'POST',
      body: form,
    })
    success.value = true
  } catch (e: any) {
    errorMessage.value =
      e?.data?.message || 'Ein Fehler ist aufgetreten. Bitte versuche es später erneut.'
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <div class="w-full max-w-[460px] md:max-w-[760px]">
    <div
      class="rounded-[16px] bg-white p-6 md:p-8"
      :style="
        props.overlay
          ? 'box-shadow: 0 24px 60px -16px rgba(0, 0, 0, 0.28), 0 6px 18px rgba(0, 0, 0, 0.08); border: 1px solid rgba(0, 0, 0, 0.06)'
          : 'box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08); border: 1px solid #eeeeee'
      "
    >
      <template v-if="!success">
        <p class="text-center text-text-muted md:text-xl">
          Willkommen im Team.<br /> Projektmanager bei ito consult.
        </p>
        <h2 class="mt-2 text-center text-[22px] font-semibold leading-[1.2] text-text md:text-[35px]">
          Hier direkt online bewerben:
        </h2>
        <!-- From sm up both items sit on ONE line side by side, per the mockup.
             On phones they stack, and the type drops to 13px: at 14px the longer
             item needs exactly the card's 279px of inner width, so it wrapped to
             a second line. 13px brings it to 261px — one line with room to spare.
             `w-fit mx-auto` centres the pair as a block while keeping both check
             marks on the same vertical line (centring each row individually put
             them at different x). -->
        <ul class="mx-auto mt-3 flex w-fit flex-col items-start gap-1.5  sm:mx-0 sm:w-auto sm:flex-row sm:flex-wrap sm:justify-center sm:gap-x-6 sm:text-sm">
          <li class="flex items-start gap-2 text-left text-text">
            <ClientOnly>
              <PhCheck weight="bold" class="mt-0.5 h-4 w-4 shrink-0 text-coral" />
              <template #fallback>
                <span class="mt-0.5 inline-block h-4 w-4 shrink-0 rounded-full bg-coral/30" />
              </template>
            </ClientOnly>
            In nur 60 Sekunden bewerben
          </li>
          <li class="flex items-start gap-2 text-left text-text">
            <ClientOnly>
              <PhCheck weight="bold" class="mt-0.5 h-4 w-4 shrink-0 text-coral" />
              <template #fallback>
                <span class="mt-0.5 inline-block h-4 w-4 shrink-0 rounded-full bg-coral/30" />
              </template>
            </ClientOnly>
            kein Anschreiben oder Lebenslauf nötig
          </li>
        </ul>

        <FormProgress :current="currentStep" :total="5" class="mt-6" />

        <!-- Step 1: Vorname + Nachname -->
        <div v-if="currentStep === 1" class="mt-6  grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField
            v-model="fields.firstname"
            label="Dein Vorname "
            name="firstname"
            required
            placeholder="Max"
            :error="errors.firstname"
          />
        
          <FormField
            v-model="fields.lastname"
            label="Dein Nachname "
            name="lastname"
            required
            placeholder="Mustermann"
            :error="errors.lastname"
          />
        </div>

        <!-- Step 2: Skills (multi-select) -->
        <CheckboxGroup
          v-else-if="currentStep === 2"
          v-model="fields.skills"
          :options="skillOptions"
          legend="Womit hast du bereits gearbeitet?"
          multiple
          class="mt-6"
        />

        <!-- Step 3: Experience (single-select) -->
        <CheckboxGroup
          v-else-if="currentStep === 3"
          v-model="fields.experience"
          :options="experienceOptions"
          legend="Wie viele Jahre Erfahrung hast du im Projektmanagement?"
          class="mt-6"
        />

        <!-- Step 4: E-Mail -->
        <FormField
          v-else-if="currentStep === 4"
          v-model="fields.email"
          label="Deine E-Mail "
          name="email"
          type="email"
          required
          placeholder="max.mustermann@mail.de"
          :error="errors.email"
          class="mt-6"
        />

        <!-- Step 5: Telefon + CV (optional) + Einwilligung + submit -->
        <div v-else class="mt-6 flex flex-col gap-4">
          <FormField
            v-model="fields.telefon"
            label="Deine Telefonnummer "
            name="telefon"
            type="tel"
            placeholder="0123/4567890"
            :error="errors.telefon"
          />
          <FileUpload
            v-model="cv"
            name="cv"
            label="Lebenslauf (PDF, optional)"
            accept="application/pdf"
            :max-size="10 * 1024 * 1024"
          />
          <FormTextarea
            v-model="fields.note"
            label="Sonstige Anmerkungen"
            name="note"
            
            placeholder="Optional"
            :error="errors.note"
          />
          <div>
            <label class="flex cursor-pointer items-start gap-2">
              <input
                v-model="fields.einwilligung"
                type="checkbox"
                class="mt-0.5 h-[18px] w-[18px] shrink-0 accent-coral"
              />
              <span class="text-sm leading-snug text-text-muted">
                Ich bin mit der
                <a
                  href="https://ito-consult.de/datenschutz"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-coral underline underline-offset-2 hover:no-underline"
                  @click.stop
                >
                  Verarbeitung meiner Daten
                </a>
                zur Bearbeitung dieser Bewerbung einverstanden.<span class="text-red-500">*</span>
              </span>
            </label>
            <p v-if="errors.einwilligung" class="mt-1 text-xs text-red-500">
              {{ errors.einwilligung }}
            </p>
          </div>
        </div>

        <div class="mt-6 flex items-center justify-between gap-3">
          <button
            v-if="currentStep > 1"
            type="button"
            class="cursor-pointer rounded-full px-3 py-2 text-sm font-medium text-text-muted active:scale-[0.98] transition-transform"
            @click="prevStep"
          >
            Zurück
          </button>
          <!-- On step 1 there is no "Zurück" button yet, so on phones the CTA
               centres instead of hanging on the right edge. From sm up — and on
               every later step, where "Zurück" holds the left side — it stays
               right-aligned so the two buttons keep their geometry. -->
          <button
            type="button"
            :disabled="submitting"
            class="inline-flex h-12 cursor-pointer items-center gap-2 rounded-full px-6 text-white text-sm font-semibold active:scale-[0.98] transition-transform disabled:cursor-not-allowed disabled:opacity-50"
            :class="currentStep === 1 ? 'mx-auto sm:ml-auto sm:mr-0' : 'ml-auto'"
            style="background: #ff5763; font-family: var(--font-roboto)"
            @click="currentStep === 5 ? submit() : nextStep()"
          >
            {{ submitting ? 'Wird gesendet…' : buttonLabel }}
          </button>
        </div>

        <p v-if="errorMessage" class="mt-4 text-center text-sm text-red-500">
          {{ errorMessage }}
        </p>
      </template>

      <div v-else class="flex flex-col items-center gap-3 py-10 text-center">
        <ClientOnly>
          <PhCheck weight="bold" class="h-12 w-12 text-success" />
          <template #fallback>
            <span class="inline-block h-12 w-12 rounded-full bg-success/30" />
          </template>
        </ClientOnly>
        <p class="text-lg font-semibold text-text">Vielen Dank!</p>
        <p class="text-sm text-text-muted">
          Deine Bewerbung wurde erfolgreich übermittelt.
        </p>
      </div>
    </div>
  </div>
</template>
